import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { useForm, usePage } from "@inertiajs/react";
import { Fragment, memo, useContext, useRef, useEffect } from "react";
import { z } from "zod";

import BB_Button from "@/Components/BB_Button";
import { ModalContext } from "@/ContextProviders/ModalProvider";
import PriceIcon from "@/Icons/price.svg?react";
import UpdateIcon from "@/Icons/update.svg?react";

const UpdateLinkFormInputs = z.object({
	price: z
		.number({message: "Der Preis muss angegeben werden"})
		.optional(),
	available: z
		.boolean({message: "Die Verfügbarkeit muss angegeben werden"}),
	link_id: z
		.number({message: "Die ID des Links muss vorhanden sein"})
		.nonnegative({ message: "Die ID des Links darf nicht ungültig sein" }),
	_token: z
		.string({ message: "Das Sicherheits-Token muss vorhanden sein" })
		.nonempty({ message: "Das Sicherheits-Token darf nicht leer sein" })
});

type UpdateLinkFormSchemaType = z.infer<typeof UpdateLinkFormInputs>;
type UpdateLinkFieldName = keyof UpdateLinkFormSchemaType;

type LinkModerationModalProps = object;

const LinkModerationModal = (_props: LinkModerationModalProps) => {
	const pageProps = usePage();
	const { showLinkModerationModal, setShowLinkModerationModal, setModeratedLink, moderatedLink } =
		useContext(ModalContext);
	const formRef = useRef<HTMLFormElement>(null);

	const updateLinkForm = useForm<UpdateLinkFormSchemaType>({
		price: undefined,
		available: moderatedLink?.in_stock !== 1,
		link_id: moderatedLink?.id ?? -1,
		_token: pageProps.props.session.csrf
	});

	useEffect(() => {
		updateLinkForm.setData({
			price: undefined,
			available: moderatedLink?.in_stock !== 1,
			link_id: moderatedLink?.id ?? -1,
			_token: pageProps.props.session.csrf
		})
	}, [moderatedLink])

	const validateUpdateLinkField = (field: UpdateLinkFieldName, value: unknown) => {
		if (value === undefined || value === null || value === "") {
			updateLinkForm.clearErrors(field);
			return;
		}

		const parsed = UpdateLinkFormInputs.safeParse({ [field]: value });

		if (parsed.success) {
			updateLinkForm.clearErrors(field);
			return;
		}

		const error = parsed.error?.flatten().fieldErrors[field]?.[0];
		if (error) {
			updateLinkForm.setError(field, error);
		}
	};

	const submitUpdateLink = () => {
        if (updateLinkForm.data.available && !confirm("Sicher, dass du den Status auf \"bestellbar\" ändern und somit Alarme auslösen möchtest?")) {
            return;
        }
		// Perform client-side validation using Zod
		const result = UpdateLinkFormInputs.safeParse(updateLinkForm.data);

		// show clientside errors
		if (!result.success) {
			const newErrors = result.error.formErrors.fieldErrors;
			Object.keys(newErrors).forEach((key) => {
				if ((key === "price" || key === "available") && newErrors[key]) {
					updateLinkForm.setError(key, newErrors[key][0]);
				}
			});
			return;
		}

		updateLinkForm.clearErrors();

		updateLinkForm.post(route("post.availability"), {
			onError: () => {
				console.log("[😕] Error while updating link");
			},
			onSuccess: () => {
				setShowLinkModerationModal(false);
				updateLinkForm.reset();
				setModeratedLink(null);
			}
		});
	};

	return (
		<Transition show={showLinkModerationModal} as={Fragment} appear>
			<Dialog as="div" className="relative" onClose={() => {
				setShowLinkModerationModal(false);
				updateLinkForm.reset();
				setModeratedLink(null);
			}}>
				{/* Backdrop */}
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-40 dark:opacity-50"
					leave="ease-in duration-200"
					leaveFrom="opacity-40 dark:opacity-50"
					leaveTo="opacity-0">
					<div className="fixed inset-0 z-20 bg-black/40 backdrop-blur-xs transition-opacity dark:bg-black/50" />
				</TransitionChild>

				{/* Centered Dialog */}
				<div className="fixed inset-0 z-30 flex items-center justify-center p-4">
					<TransitionChild
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95">
						<DialogPanel className="bg-background-100 dark:bg-dark-background-300 w-full max-w-md rounded-lg p-6 shadow-lg ring-1 ring-gray-900/5">
							<DialogTitle className="text-lg font-medium text-gray-900 dark:text-gray-200">
                                Verfügbarkeit melden
							</DialogTitle>
							<p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
								Produkt: <i>{moderatedLink?.title ?? "Unbekannt"}</i>
								<br />
								<br />
								Händler: <i>{moderatedLink?.store.name ?? "Unbekannt"}</i>
							</p>

							<form
								ref={formRef}
								noValidate
								className="grid grid-cols-1"
							>

                                <div>
                                    <label
                                        htmlFor="password"
                                        className="mt-4 mb-3 block text-sm font-medium text-gray-900 dark:text-gray-200">
                                        Preis (optional)
                                    </label>
                                    <div className="relative">
                                        <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3.5">
                                            <PriceIcon className="size-4" />
                                        </div>
                                        <input
                                            value={
                                                updateLinkForm.data.price ?? ""
                                            }
                                            onChange={(e) =>
                                                updateLinkForm.setData(
                                                    "price",
                                                    e.target.value ? parseFloat(e.target.value) : undefined
                                                )
                                            }
                                            onFocus={() =>
                                                updateLinkForm.clearErrors("price")
                                            }
                                            onBlur={(e) =>
                                                validateUpdateLinkField(
                                                    "price",
                                                    e.target.value ? parseFloat(e.target.value) : undefined
                                                )
                                            }
                                            id="price"
                                            name="price"
                                            type="number"
                                            placeholder={moderatedLink?.price?.toString() ?? "-"}
                                            autoComplete="off"
                                            className="bg-background-50 dark:bg-dark-background-400 focus:ring-primary-500 block w-full rounded-md border-0 py-1.5 ps-10 text-black shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 dark:text-white"
                                        />
                                    </div>
                                </div>

								{updateLinkForm.errors.price && (
									<span className="text-sm text-red-500">
										{updateLinkForm.errors.price}
									</span>
								)}

								<div>
									<label
										htmlFor="available"
										className="mt-4 mb-3 block text-sm font-medium text-gray-900 dark:text-gray-200">
										Status Verfügbarkeit
									</label>
									<select
										value={updateLinkForm.data.available ? "in_stock" : "out_of_stock"}
										onChange={(e) =>
											updateLinkForm.setData(
												"available",
												e.target.value === "in_stock"
											)
										}
										onFocus={() =>
											updateLinkForm.clearErrors("available")
										}
										onBlur={(e) =>
											validateUpdateLinkField(
												"available",
												e.target.value === "in_stock"
											)
										}
										id="available"
										name="available"
										className="bg-background-50 dark:bg-dark-background-400 focus:ring-primary-500 block w-full rounded-md border-0 py-1.5 ps-3 text-black shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 dark:text-white"
									>
										<option value="in_stock">Bestellbar</option>
										<option value="out_of_stock">Nicht bestellbar</option>
									</select>
								</div>

								{updateLinkForm.errors.available && (
									<span className="text-sm text-red-500">
										{updateLinkForm.errors.available}
									</span>
								)}

								<div className="flex justify-between space-x-3 mt-6">
									<BB_Button
										type="secondary"
										buttonClassName="flex-1"
										disabled={updateLinkForm.processing}
										title="Abbrechen"
										onClick={() => {
											setShowLinkModerationModal(false);
											updateLinkForm.reset();
											setModeratedLink(null);
										}}
									/>
									<BB_Button
										type={updateLinkForm.data.available ? 'destructive' : 'primary'}
										onClick={submitUpdateLink}
										disabled={updateLinkForm.processing}
										buttonClassName="flex-1"
										icon={UpdateIcon}
										iconClassName={"h-5 w-5 mr-2 pointer-events-none"}
										title="Speichern"
										ripple={true}
									/>
								</div>

								{ updateLinkForm.data.available &&
									<div className="mt-4 text-sm text-primary-500 text-pretty">
                                        <i>Achtung: Ändern des Status auf "bestellbar" versendet E-Mails und Push-Benachrichtigungen!</i>
                                    </div>
								}
							</form>
						</DialogPanel>
					</TransitionChild>
				</div>
			</Dialog>
		</Transition>
	);
};

export default memo(LinkModerationModal);
